import { Parser } from 'expr-eval';
import gridSizing from './gridSizing';

type MustacheFuncWithParams<Params extends unknown | void> = (
  text: string,
  render: (text: string) => string,
  params: Params
) => string;
export type MustacheFunc = (text: string, render: (text: string) => string) => string;

function renderParamStringsOnce<T extends Record<string, unknown>>(obj: T, render: (s: string) => string): T {
  if (!obj) return obj;
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? render(v) : v])) as T;
}

export const createMustacheFunc: <Params extends unknown | void>(func: MustacheFuncWithParams<Params>) => MustacheFunc =
  (func) => (text, render) => {
    const [params, content] = JSON.parse(text);
    const parsedParams = renderParamStringsOnce(params, render);

    return func(content, render, parsedParams);
  };

export type MustacheFuncs = 'replace' | 'calc' | 'length' | 'measureTable' | 'equal';

const funcs: Record<MustacheFuncs, () => MustacheFunc> = {
  replace: <T extends { replaceTarget: string; replaceWith: string }>() =>
    createMustacheFunc((text, render, params: T) => {
      return render(text).replace(params.replaceTarget, params.replaceWith);
    }),
  calc: <T extends { expr: string }>() =>
    createMustacheFunc((_, __, params: T) => {
      return String(Parser.evaluate(params.expr));
    }),
  length: () =>
    createMustacheFunc((text, render) => {
      return render(text).length.toString();
    }),
  equal: <T extends { a: string; b: string; invert: boolean }>() =>
    createMustacheFunc((text, render, { a, b, invert = false }: T) => {
      const result =
        String(render(a) ?? '')
          .trim()
          .toLowerCase() ===
        String(render(b) ?? '')
          .trim()
          .toLowerCase();

      if (result !== invert) {
        return render(text);
      }

      return '';
    }),
  measureTable: gridSizing,
};

export default funcs;
